import React, { useEffect, useRef, useState } from "react";
import { BACKEND_URL } from "@/lib/backendUrl";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { useMixer } from "@/context/MixerContext";
import { useAuth } from "@/context/AuthContext";
import { Radio, Copy, StopCircle, Loader2, Share2, DollarSign } from "lucide-react";

const API = `${BACKEND_URL}/api`;
const WS_BASE = BACKEND_URL.replace(/^http/, "ws");

/**
 * Go Live button for the DJ. Starts a live session, opens a WebSocket
 * broadcast connection, records the master output with MediaRecorder
 * (timeslice=1000ms) and pushes each chunk to listeners via the backend.
 */
export default function GoLiveButton({ decks, token }) {
  const { engine } = useMixer();
  const { user, refreshUser } = useAuth();
  const [session, setSession] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [tipGoal, setTipGoal] = useState("");
  const [merchItems, setMerchItems] = useState([{ title: "", price: "", description: "" }]);
  const [slowMode, setSlowMode] = useState("0");
  // Payout region — determines which currency/provider tippers see on the
  // public tip-jar. Persisted on the user record via /me/payout-region so
  // it survives sessions. Mobile-money fields are only required when the
  // region isn't Global (USD via Stripe Connect).
  const [payoutRegion, setPayoutRegion] = useState(user?.payout_region || "global");
  const [mmName, setMmName] = useState(user?.mobile_money_name || "");
  const [mmNumber, setMmNumber] = useState(user?.mobile_money_number || "");
  // Uganda has two providers on one currency; Kenya only has M-Pesa so the
  // selector is only rendered for the Uganda branch.
  const [mmProvider, setMmProvider] = useState(user?.mobile_money_provider || "mtn_momo");
  // Regions Pesapal is armed for on this deploy — Uganda is gated until we
  // ship a Uganda-specific merchant profile (cross-border FX otherwise).
  const [regionsEnabled, setRegionsEnabled] = useState({ kenya: true, uganda: false });
  useEffect(() => {
    axios.get(`${API}/pesapal/status`).then(r => {
      if (r.data?.regions_enabled) setRegionsEnabled(r.data.regions_enabled);
    }).catch(() => {});
  }, []);
  // Keep local state in sync when the user record refreshes after saving.
  useEffect(() => {
    if (!user) return;
    setPayoutRegion(user.payout_region || "global");
    setMmName(user.mobile_money_name || "");
    setMmNumber(user.mobile_money_number || "");
    setMmProvider(user.mobile_money_provider || "mtn_momo");
  }, [user?.payout_region, user?.mobile_money_name, user?.mobile_money_number, user?.mobile_money_provider]);
  const wsRef = useRef(null);
  const recRef = useRef(null);
  const shareUrlRef = useRef("");
  // Set to true by stopLive() so onclose knows this was intentional and
  // shouldn't trigger the reconnect loop.
  const wantStopRef = useRef(false);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const sessionRef = useRef(null);  // mirror of `session` state for closures
  const tokenRef = useRef(token);

  useEffect(() => { tokenRef.current = token; }, [token]);
  useEffect(() => { sessionRef.current = session; }, [session]);

  /**
   * Reconnect the broadcast WebSocket + MediaRecorder to the current
   * session. Called both on the initial "go live" and on any subsequent
   * auto-reconnect after a network blip. The backend keeps the session
   * alive for 5 min after our socket drops (see routers/live.py grace
   * period), so as long as we get a fresh WS attached within that window
   * listeners never notice.
   */
  const openBroadcastSocket = (sid) => {
    const ws = new WebSocket(`${WS_BASE}/api/live/broadcast/${sid}?token=${encodeURIComponent(tokenRef.current)}`);
    ws.binaryType = "arraybuffer";
    wsRef.current = ws;
    ws.onopen = () => {
      reconnectAttemptsRef.current = 0;
      // Stop any prior MediaRecorder (reconnect path) before starting a fresh one.
      try { recRef.current?.stop(); } catch { /* noop */ }
      const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/webm";
      const rec = new MediaRecorder(engine.recDest.stream, { mimeType: mime });
      rec.ondataavailable = async (e) => {
        if (!e.data || e.data.size === 0) return;
        if (ws.readyState !== 1) return;
        const buf = await e.data.arrayBuffer();
        try { ws.send(buf); } catch { /* WS closed between check + send */ }
      };
      rec.start(1000);  // 1-second chunks
      recRef.current = rec;
    };
    ws.onclose = () => {
      if (wantStopRef.current) {
        toast.info("Live session ended.");
        return;
      }
      // Attempt automatic reconnection: exponential-ish backoff, cap 60s.
      // Server holds the session for 5 min in grace, so we have plenty of
      // room to recover from a mobile-signal / tab-background blip.
      const attempt = reconnectAttemptsRef.current + 1;
      reconnectAttemptsRef.current = attempt;
      if (attempt === 1) toast.warning("Live connection dropped — reconnecting…");
      if (attempt > 20) {
        toast.error("Reconnect failed — please tap Go Live again");
        wantStopRef.current = false;
        return;
      }
      const delayMs = Math.min(3000 * attempt, 15000);
      reconnectTimerRef.current = setTimeout(() => openBroadcastSocket(sid), delayMs);
    };
    ws.onerror = () => { /* onclose fires immediately after — retry lives there */ };
  };

  const addMerchRow = () => {
    if (merchItems.length >= 3) return;
    setMerchItems([...merchItems, { title: "", price: "", description: "" }]);
  };
  const removeMerchRow = (i) => setMerchItems(merchItems.filter((_, idx) => idx !== i));
  const updateMerchRow = (i, key, val) => {
    const next = [...merchItems];
    next[i] = { ...next[i], [key]: val };
    setMerchItems(next);
  };

  const goLive = async () => {
    if (!engine) { toast.error("Mixer not ready"); return; }
    // Persist payout region + mobile-money account before starting the
    // broadcast so the public tip page is already showing the right
    // currency/provider the moment listeners land on the share link.
    if (payoutRegion !== "global") {
      if (!mmName.trim() || !mmNumber.trim()) {
        toast.error("Enter your mobile-money name + number before going live");
        return;
      }
    }
    setBusy(true);
    try {
      try {
        await axios.post(`${API}/me/payout-region`, {
          payout_region: payoutRegion,
          mobile_money_name: mmName.trim() || null,
          mobile_money_number: mmNumber.trim() || null,
          mobile_money_provider: payoutRegion === "uganda" ? mmProvider : (payoutRegion === "kenya" ? "mpesa" : null),
        });
        if (refreshUser) await refreshUser();
      } catch (e) {
        toast.error(e.response?.data?.detail || "Could not save payout region — try again");
        setBusy(false);
        return;
      }
      const r = await axios.post(`${API}/live/start`, {});
      const sid = r.data.session_id;
      const shareUrl = `${window.location.origin}/live/${sid}`;
      shareUrlRef.current = shareUrl;
      setSession({ id: sid, url: shareUrl });
      setModalOpen(true);

      // Save tip goal + merch config for this session
      const goal = parseInt(tipGoal, 10) || 0;
      const validMerch = merchItems
        .map(m => ({
          title: (m.title || "").trim(),
          price_cents: Math.round(parseFloat(m.price) * 100) || 0,
          description: (m.description || "").trim(),
        }))
        .filter(m => m.title && m.price_cents > 0);
      const slow = Math.max(0, Math.min(60, parseInt(slowMode, 10) || 0));
      if (goal > 0 || validMerch.length > 0 || slow > 0) {
        try {
          await axios.post(`${API}/live/${sid}/meta`, {
            tip_goal_cents: goal * 100,
            merch_items: validMerch,
            slow_mode_seconds: slow,
          });
        } catch {}
      }

      // Open broadcaster socket (with auto-reconnect wired in — see
      // openBroadcastSocket. First-connect success shows the toast).
      await engine.resume();
      wantStopRef.current = false;
      reconnectAttemptsRef.current = 0;
      openBroadcastSocket(sid);
      toast.success("You're live.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to go live");
    } finally { setBusy(false); }
  };

  // Broadcast track changes as metadata
  useEffect(() => {
    if (!session || !wsRef.current || wsRef.current.readyState !== 1) return;
    const playing = ["A", "B", "C", "D"].map(id => ({ id, d: decks[id] })).find(x => x.d.playing && x.d.track);
    if (!playing) return;
    try {
      wsRef.current.send(JSON.stringify({ latest_track: {
        deck_id: playing.id,
        title: playing.d.track.title,
        artist: playing.d.track.artist,
        bpm: playing.d.track.bpm || null,
        key: playing.d.track.key || null,
      } }));
    } catch {}
    // eslint-disable-next-line
  }, [session, decks.A.track?.id, decks.A.playing, decks.B.track?.id, decks.B.playing, decks.C.track?.id, decks.C.playing, decks.D.track?.id, decks.D.playing]);

  const stopLive = async () => {
    // Tell the server to end the session FIRST (records replay, drops LIVE_SESSIONS).
    // If we close our WS first, the broadcast_ws finally-block will GC the session
    // before our POST /stop hits — leading to a 404.
    // We also flip `wantStopRef` so any late onclose fired during teardown
    // does NOT kick off the auto-reconnect loop.
    wantStopRef.current = true;
    if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); reconnectTimerRef.current = null; }
    if (session?.id) {
      try {
        await axios.post(`${API}/live/${session.id}/stop`);
        toast.success("Broadcast ended.");
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to end session cleanly");
      }
    }
    try { recRef.current?.stop(); } catch {}
    try { wsRef.current?.close(); } catch {}
    setSession(null);
    setModalOpen(false);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareUrlRef.current);
    toast.success("Live link copied.");
  };

  // ---- Social share ----
  const shareText = () => `I'm live on NeuralDeck right now — come listen to my mix:`;
  const openShare = (target) => {
    const url = shareUrlRef.current;
    if (!url) return;
    const enc = encodeURIComponent;
    const text = shareText();
    const map = {
      whatsapp: `https://wa.me/?text=${enc(`${text} ${url}`)}`,
      x: `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(url)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
      telegram: `https://t.me/share/url?url=${enc(url)}&text=${enc(text)}`,
      // Gmail compose window (works on desktop, falls back to mailto on mobile)
      gmail: `https://mail.google.com/mail/?view=cm&fs=1&su=${enc("I'm live on NeuralDeck")}&body=${enc(`${text}\n\n${url}`)}`,
      email: `mailto:?subject=${enc("I'm live on NeuralDeck")}&body=${enc(`${text}\n\n${url}`)}`,
    };
    const to = map[target];
    if (!to) return;
    if (target === "email") {
      window.location.href = to;
    } else {
      window.open(to, "_blank", "noopener,noreferrer,width=640,height=640");
    }
  };

  const nativeShare = async () => {
    const url = shareUrlRef.current;
    if (!url) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: "NeuralDeck LIVE", text: shareText(), url });
      } catch { /* user cancelled */ }
    } else {
      copyLink();
    }
  };

  return (
    <>
      {!session ? (
        <>
          <Button data-testid="go-live-button" onClick={() => setModalOpen(true)} disabled={busy || !token} variant="outline"
            className="border-[#EF4444] hover:bg-[#EF4444] hover:text-white text-[#EF4444] font-black uppercase tracking-widest text-xs">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin"/> : <Radio className="w-4 h-4 mr-1.5"/>} Go Live
          </Button>
          <Dialog open={modalOpen && !session} onOpenChange={setModalOpen}>
            <DialogContent className="bg-[#121620] border-[#EF4444]/40 text-white sm:max-w-md" data-testid="go-live-setup-modal">
              <DialogHeader>
                <DialogTitle className="font-display font-black text-2xl">Set Up Live Session</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 pt-2">
                {/* PAYOUT REGION — Africa mobile money or Global USD via Stripe */}
                <div className="space-y-2" data-testid="go-live-payout-region-section">
                  <div className="text-xs uppercase tracking-widest text-[#B6FF3C] font-mono">// Payout Region</div>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      { id: "global",  label: "🌍 Global",   sub: "USD · Stripe",  enabled: true },
                      { id: "kenya",   label: "🇰🇪 Kenya",    sub: "KES · M-Pesa",  enabled: regionsEnabled.kenya },
                      { id: "uganda",  label: "🇺🇬 Uganda",   sub: regionsEnabled.uganda ? "UGX · MTN/Airtel" : "Coming soon", enabled: regionsEnabled.uganda },
                    ].map(r => (
                      <button key={r.id} type="button"
                        disabled={!r.enabled}
                        data-testid={`go-live-region-${r.id}`}
                        onClick={() => r.enabled && setPayoutRegion(r.id)}
                        title={!r.enabled ? "Uganda payouts land shortly — we're finalising our Uganda Pesapal profile so tips settle natively in UGX with MTN MoMo support." : ""}
                        className={`p-2.5 rounded border transition-colors text-left ${
                          !r.enabled
                            ? "border-[#1E2638] text-[#475569] cursor-not-allowed opacity-50"
                            : payoutRegion === r.id
                              ? "border-[#B6FF3C] bg-[#B6FF3C]/10 text-white"
                              : "border-[#2A354B] text-[#94A3B8] hover:border-[#B6FF3C]/50"
                        }`}>
                        <div className="text-[13px] font-black">{r.label}</div>
                        <div className="text-[9px] font-mono uppercase tracking-widest mt-0.5">{r.sub}</div>
                      </button>
                    ))}
                  </div>
                  {payoutRegion !== "global" && (
                    <div className="space-y-2 mt-2 p-2.5 rounded border border-[#B6FF3C]/30 bg-[#B6FF3C]/5">
                      {payoutRegion === "uganda" && (
                        <div className="flex gap-2">
                          {[
                            { id: "mtn_momo",     label: "MTN MoMo" },
                            { id: "airtel_money", label: "Airtel Money" },
                          ].map(p => (
                            <button key={p.id} type="button"
                              data-testid={`go-live-provider-${p.id}`}
                              onClick={() => setMmProvider(p.id)}
                              className={`flex-1 text-[10px] font-black px-2 py-1.5 rounded border uppercase tracking-widest transition-colors ${
                                mmProvider === p.id
                                  ? "border-[#B6FF3C] bg-[#B6FF3C] text-black"
                                  : "border-[#2A354B] text-[#94A3B8] hover:border-[#B6FF3C]"
                              }`}>{p.label}</button>
                          ))}
                        </div>
                      )}
                      <input value={mmName} onChange={e => setMmName(e.target.value)}
                        placeholder="Full name on the mobile-money account"
                        data-testid="go-live-mm-name"
                        className="w-full bg-[#0B0E14] border border-[#2A354B] text-white px-3 py-2 rounded text-sm"/>
                      <input value={mmNumber} onChange={e => setMmNumber(e.target.value)}
                        placeholder={payoutRegion === "kenya" ? "+2547XX XXX XXX" : "+2567X XXX XXXX"}
                        data-testid="go-live-mm-number"
                        className="w-full bg-[#0B0E14] border border-[#2A354B] text-white px-3 py-2 rounded text-sm font-mono"/>
                      <div className="text-[10px] font-mono text-[#94A3B8]">
                        Tippers see local pricing + pay with {payoutRegion === "kenya" ? "M-Pesa" : "MTN MoMo or Airtel Money"}.
                        Platform 10% goes to your Flutterwave balance — you withdraw whenever you want.
                      </div>
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-widest text-[#FFC107] font-mono">// Tip Goal (optional)</div>
                  <input value={tipGoal} onChange={e => setTipGoal(e.target.value)} type="number" min="0" step="10" placeholder="e.g. 50" data-testid="go-live-tip-goal"
                    className="w-full bg-[#0B0E14] border border-[#2A354B] text-white px-3 py-2 rounded"/>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="text-xs uppercase tracking-widest text-[#10B981] font-mono">// Merch Drops (up to 3)</div>
                    {merchItems.length < 3 && (
                      <button type="button" data-testid="go-live-merch-add" onClick={addMerchRow}
                        className="text-[10px] font-mono uppercase tracking-widest text-[#10B981] hover:text-white">+ Add</button>
                    )}
                  </div>
                  {merchItems.map((m, i) => (
                    <div key={i} className="space-y-1.5 p-2 rounded border border-[#1E2638] bg-[#0B0E14]/60" data-testid={`go-live-merch-row-${i}`}>
                      <div className="flex items-center gap-2">
                        <input value={m.title} onChange={e => updateMerchRow(i, "title", e.target.value)}
                          placeholder={`Item ${i + 1} title`} data-testid={`go-live-merch-title-${i}`}
                          className="flex-1 bg-[#0B0E14] border border-[#2A354B] text-white px-3 py-2 rounded"/>
                        {merchItems.length > 1 && (
                          <button type="button" data-testid={`go-live-merch-remove-${i}`} onClick={() => removeMerchRow(i)}
                            className="text-[#EF4444] text-xs font-bold px-2 py-1">×</button>
                        )}
                      </div>
                      <div className="flex gap-2">
                        <input value={m.price} onChange={e => updateMerchRow(i, "price", e.target.value)}
                          type="number" min="0" step="1" placeholder="USD" data-testid={`go-live-merch-price-${i}`}
                          className="w-24 bg-[#0B0E14] border border-[#2A354B] text-white px-3 py-2 rounded"/>
                        <input value={m.description} onChange={e => updateMerchRow(i, "description", e.target.value)}
                          placeholder="Short description (optional)" data-testid={`go-live-merch-desc-${i}`}
                          className="flex-1 bg-[#0B0E14] border border-[#2A354B] text-white px-3 py-2 rounded"/>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="space-y-2">
                  <div className="text-xs uppercase tracking-widest text-[#00F0FF] font-mono">// Chat Slow Mode (seconds, 0 = off)</div>
                  <input value={slowMode} onChange={e => setSlowMode(e.target.value)} type="number" min="0" max="60" step="1" data-testid="go-live-slow-mode"
                    className="w-full bg-[#0B0E14] border border-[#2A354B] text-white px-3 py-2 rounded"/>
                </div>
                <Button data-testid="go-live-confirm-button" onClick={goLive} disabled={busy}
                  className="w-full bg-[#EF4444] text-white hover:bg-[#DC2626] font-black uppercase tracking-widest py-6">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <><Radio className="w-4 h-4 mr-2"/> Start Broadcast</>}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </>
      ) : (
        <Button data-testid="go-live-active-button" onClick={() => setModalOpen(true)}
          className="bg-[#EF4444] text-white hover:bg-[#DC2626] font-black uppercase tracking-widest text-xs animate-pulse">
          <Radio className="w-4 h-4 mr-1.5"/> LIVE
        </Button>
      )}

      <Dialog open={modalOpen && !!session} onOpenChange={setModalOpen}>
        <DialogContent className="bg-[#121620] border-[#EF4444]/40 text-white sm:max-w-md" data-testid="live-modal">
          <DialogHeader>
            <DialogTitle className="font-display font-black text-2xl flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-[#EF4444] animate-pulse"/> You're Live
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="text-sm text-[#94A3B8]">Share this link — anyone who opens it will hear your live mix in near real-time.</div>
            <div className="flex items-center gap-2 p-3 rounded-lg border border-[#EF4444]/40 bg-[#EF4444]/5 font-mono text-xs break-all" data-testid="live-share-url">
              <Radio className="w-4 h-4 flex-shrink-0 text-[#EF4444]"/>
              <span className="flex-1">{shareUrlRef.current}</span>
            </div>

            {/* ---------- Dual QR: Listen + Tip Jar ---------- */}
            <div className="grid grid-cols-2 gap-3" data-testid="live-qr-row">
              <div className="flex flex-col items-center gap-2 p-3 rounded-xl bg-white/95" data-testid="live-listen-qr-wrap">
                <QRCodeSVG value={shareUrlRef.current} size={140} level="M"
                  bgColor="#ffffff" fgColor="#0B0E14" data-testid="live-listen-qr"/>
                <div className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#0B0E14] font-black">
                  <Radio className="w-3 h-3"/> Listen
                </div>
              </div>
              {user?.handle && user?.stripe_connect?.charges_enabled ? (
                <div className="flex flex-col items-center gap-2 p-3 rounded-xl bg-white/95" data-testid="live-tip-qr-wrap">
                  <QRCodeSVG value={`${window.location.origin}/tip/${user.handle}`}
                    size={140} level="M" bgColor="#ffffff" fgColor="#0B0E14"
                    data-testid="live-tip-qr"/>
                  <div className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#0B0E14] font-black">
                    <DollarSign className="w-3 h-3"/> Tip jar
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center gap-2 p-3 rounded-xl border border-dashed border-[#94A3B8]/30 text-center" data-testid="live-tip-qr-disabled">
                  <DollarSign className="w-6 h-6 text-[#94A3B8]"/>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-[#94A3B8]">
                    Connect Stripe<br/>in Settings<br/>to enable tips
                  </div>
                </div>
              )}
            </div>
            <div className="text-[10px] text-center text-[#94A3B8] font-mono uppercase tracking-widest">
              Project this on a screen · crowd scans to listen or tip
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Button data-testid="live-copy-link" onClick={copyLink} className="bg-[#00F0FF] text-black hover:bg-[#33F3FF] font-black uppercase tracking-widest">
                <Copy className="w-4 h-4 mr-2"/> Copy Link
              </Button>
              <Button data-testid="live-stop-button" onClick={stopLive} variant="outline" className="border-[#EF4444] text-[#EF4444] hover:bg-[#EF4444] hover:text-white font-black uppercase tracking-widest">
                <StopCircle className="w-4 h-4 mr-2"/> End Live
              </Button>
            </div>

            {/* ---------- Social share row ---------- */}
            <div className="pt-2 border-t border-[#1E2638]" data-testid="live-share-row">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#94A3B8]">// Share to</div>
                {typeof navigator !== "undefined" && navigator.share && (
                  <button type="button" data-testid="live-share-native"
                    onClick={nativeShare}
                    className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-[#00F0FF] hover:text-white">
                    <Share2 className="w-3 h-3"/> More…
                  </button>
                )}
              </div>
              <div className="grid grid-cols-5 gap-2">
                {[
                  { id: "whatsapp", label: "WhatsApp", bg: "#25D366", path: "M20.5 3.5A11.9 11.9 0 0 0 12 0 12 12 0 0 0 1.7 17.9L0 24l6.3-1.6A12 12 0 0 0 24 12a11.9 11.9 0 0 0-3.5-8.5zM12 21.8a9.8 9.8 0 0 1-5-1.4l-.4-.2-3.7 1 1-3.6-.2-.4A9.8 9.8 0 1 1 21.8 12 9.8 9.8 0 0 1 12 21.8zm5.4-7.4c-.3-.1-1.7-.9-2-1-.3-.1-.5-.1-.7.1-.2.3-.8 1-1 1.2-.2.2-.4.2-.7.1a8 8 0 0 1-4-3.5c-.3-.5.3-.5.8-1.5.1-.2 0-.4 0-.6l-1-2.3c-.3-.6-.5-.5-.7-.5h-.6a1.1 1.1 0 0 0-.8.4 3.4 3.4 0 0 0-1 2.5c0 1.5 1 2.9 1.2 3.1a11 11 0 0 0 4.3 3.8c.6.3 1 .4 1.4.5.6.2 1.1.2 1.5.1s1.7-.7 2-1.4c.2-.7.2-1.2.1-1.4-.1-.2-.3-.2-.6-.4z" },
                  { id: "x", label: "X", bg: "#1A1A1A", path: "M18.244 2H21l-6.53 7.462L22 22h-6.766l-4.985-6.51L4.55 22H2l6.983-7.983L2 2h6.914l4.514 5.964L18.244 2zm-2.373 18.375h1.65L6.31 3.53H4.54l11.331 16.845z" },
                  { id: "facebook", label: "Facebook", bg: "#1877F2", path: "M24 12A12 12 0 1 0 10.13 23.85V15.47H7.08V12h3.05V9.36c0-3 1.79-4.67 4.53-4.67 1.31 0 2.68.24 2.68.24v2.94h-1.51c-1.49 0-1.95.93-1.95 1.88V12h3.33l-.53 3.47h-2.8v8.38A12 12 0 0 0 24 12z" },
                  { id: "telegram", label: "Telegram", bg: "#26A5E4", path: "M12 0a12 12 0 1 0 0 24 12 12 0 0 0 0-24zm5.9 8.16-1.97 9.29c-.15.66-.54.82-1.09.51l-3.02-2.23-1.46 1.4c-.16.16-.3.3-.6.3l.21-3.07 5.6-5.06c.24-.22-.05-.34-.38-.13l-6.92 4.36-2.98-.93c-.65-.2-.66-.64.14-.96l11.63-4.48c.54-.2 1.01.13.84.94z" },
                  { id: "gmail", label: "Gmail", bg: "#EA4335", path: "M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z" },
                ].map(s => (
                  <button key={s.id} type="button"
                    data-testid={`live-share-${s.id}`}
                    title={`Share to ${s.label}`}
                    aria-label={`Share to ${s.label}`}
                    onClick={() => openShare(s.id)}
                    className="flex flex-col items-center gap-1 p-2 rounded-lg border border-[#1E2638] hover:border-white/60 hover:bg-white/5 transition-colors group">
                    <span className="w-9 h-9 rounded-full flex items-center justify-center ring-1 ring-white/10" style={{ backgroundColor: s.bg }}>
                      <svg viewBox="0 0 24 24" className="w-5 h-5" fill="#ffffff" aria-hidden="true"><path d={s.path}/></svg>
                    </span>
                    <span className="text-[9px] font-mono uppercase tracking-widest text-[#94A3B8] group-hover:text-white">{s.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="text-[11px] text-center text-[#94A3B8] font-mono">Broadcasting via WebSocket · listeners assemble with MSE</div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
